// this file added by Bearer Web Developer - change map api
import React, { useContext, useEffect } from 'react';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { useThemeMode } from '../../../states/contexts/theme';
import { Location, useAppState } from '../../../states/app-state';
import { useMediaQuery, useTheme } from "@mui/material";
import { observer } from 'mobx-react-lite';
import OriginIcon from "../../../assets/images/origin-map.png";
import OriginIconDarkMode from "../../../assets/images/origin-map-dark-mode.svg"; // added by Bearer Web Developer
import DestinationIcon from "../../../assets/images/destination-map.png";
import { MenuDrawerContext } from '../../../states/contexts/menuDrawer';
import Polyline from './polyline'; // added by Bearer Web Developer
import styles from './BearerMapProvider.module.scss';
import RiderMarkers from './riderMarkers';
import BearerMapProvider from './BearerMapProvider';
import scaleBounds from './scaleBounds'; // added by Bearer Web Developer
import isSafari from './isSafari'; // added by Bearer Web Developer
import ShortcutOrders from './shortcutOrders'; // added by Bearer Web Developer
import { ShortcutReservedOrdersPlan } from '../../../states/reservedOrdersState'; // added by Bearer Web Developer

const MapApi: React.FC = observer(() => {
    const map = useMap();
    const { theme: themeMode } = useThemeMode(); // added by Bearer Web Developer
    const appState = useAppState();
    const selectedOrder = appState.selectedOrder;
    const environmentCode = appState.environmentCode;
    const mapId = environmentCode === "STAGING" ? "f4e86786fbb6b3bb" :
        environmentCode === "PRODUCTION" ? "683a7c74fe921546" : "39e429275f5344f2";
    const theme = useTheme();
    const p_points = selectedOrder?.selectedTransportOption?.overview_polyline_latlng;
    const rider_polyline = selectedOrder?.rider?.riderPath?.overview_polyline; // added by Bearer Web Developer
    const getLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(showPosition);
        } else {
            console.log("Geolocation is not supported by this browser."); // modify by Bearer Web Developer
        }
    }
    const showPosition = (position: any) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const bounds = new google.maps.LatLngBounds();
        bounds.extend({ lat, lng });
    }
    function createPath(o?: Location, d?: Location): Location[] {
        return [o, d].filter(l => !!l) as Location[]
    }
    const d = selectedOrder?.destination.address?.location;
    const o = selectedOrder?.origin.address?.location;
    const r = selectedOrder?.rider.location; // added by Bearer Web Developer for extend of rider location
    const bounds = new google.maps.LatLngBounds();
    o && bounds.extend(o)
    d && bounds.extend(d)
    r && bounds.extend(r) // added by Bearer Web Developer for extend of rider location
    const pathPreview = createPath(o, d);
    // p_points?.forEach(p => bounds.extend(p)); // commented by Bearer Web Developer (for add rider_polyline condition)
    if (!rider_polyline) p_points?.forEach(p => bounds.extend(p)); // added by Bearer Web Developer (for add rider_polyline condition)
    if (rider_polyline) rider_polyline?.forEach(p => bounds.extend(p)); // added by Bearer Web Developer
    if (!r && !o && !d && !p_points && !rider_polyline) { // !r, rider_polyline  added by Bearer Web Developer for extend of rider location
        bounds.extend({
            lat: -38.608128,
            lng: 144.572079,
        });
        bounds.extend({
            lat: -37.572079,
            lng: 145.597504,
        })
    }
    const literalBounds = !isSafari() ? scaleBounds(bounds).toJSON() : bounds.toJSON(); // added by Bearer Web Developer
    // const literalBounds = bounds.toJSON(); // added and commented by Bearer Web Developer
    const options: google.maps.MapOptions = {
        zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_CENTER,
        } as google.maps.ZoomControlOptions,
        zoomControl: false,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        panControl: false,
        rotateControl: false,
        fullscreenControl: false,
        draggable: (!selectedOrder?.origin.address || !selectedOrder?.destination.address),
        // Update while changing STAGE
        // ...otherOptions
    }
    // modified by Bearer Web Developer start - add useEffect and dependencies
    useEffect(() => {
        if (map) {
            map?.setOptions(options)
            map?.fitBounds(literalBounds);
            if ((selectedOrder?.origin.address && !selectedOrder?.destination.address) ||
                (selectedOrder?.destination.address && !selectedOrder?.origin.address)) map?.setZoom(17);
        }
    }, [map, o, d, r, rider_polyline, p_points]);
    // modified by Bearer Web Developer end
    // added by Bearer Web Developer start (for mobile: after open map in mobile state, map must be rerender and show bound correctly (bound is to small))
    const { swipe } = useContext(MenuDrawerContext);
    useEffect(() => {
        if (swipe && map) {
            map?.fitBounds(literalBounds);
            if ((selectedOrder?.origin.address && !selectedOrder?.destination.address) ||
                (selectedOrder?.destination.address && !selectedOrder?.origin.address)) map?.setZoom(17);
        }
    }, [swipe]);
    // added by Bearer Web Developer end
    // added by Bearer Web Developer start
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 900px max width
    const { reservedOrdersOpen } = appState?.ReservedOrders;
    const { apiIntegrationOpen } = appState?.ApiIntegration;
    const { transportHistoryOpen } = appState?.transportHistory;
    const showscreen = (reservedOrdersOpen || apiIntegrationOpen || transportHistoryOpen) && !isMobile;
    // added by Bearer Web Developer end
    return (
        <Map
            mapId={mapId}
            colorScheme={themeMode === 'dark' ? 'DARK' : 'LIGHT'} // added by Bearer Web Developer
            style={{
                width: '100%',
                height: '100%',
                display: showscreen  ? 'none' : '' // added by Bearer Web Developer
            }}
            defaultCenter={{ lat: -37.813628, lng: 144.963058 }}
            defaultZoom={17}
            disableDefaultUI
        >
            {
                selectedOrder?.origin.address?.location && <AdvancedMarker
                    anchorLeft="-55%"
                    anchorTop="-97%"
                    position={selectedOrder.origin.address.location}>
                    <img
                        src={themeMode === "dark" ? OriginIconDarkMode : OriginIcon} // OriginIconDarkMode and themeMode condition added by Bearer Web Developer}
                        width={40}
                        height={68}
                        alt=""
                    />
                </AdvancedMarker>
            }
            {
                selectedOrder?.destination.address?.location && <AdvancedMarker
                    anchorLeft="-55%"
                    anchorTop="-97%"
                    position={selectedOrder.destination.address.location}>
                    <img
                        src={DestinationIcon}
                        width={40}
                        height={68}
                        alt=""
                    />
                </AdvancedMarker>
            }
            {/* rider markers added by Bearer Web Developer start*/}
            {selectedOrder?.rider.location && <RiderMarkers />}
            {/* rider markers added by Bearer Web Developer end*/}
            {/* rider_polyline added by Bearer Web Developer start*/}
            {rider_polyline &&
                <Polyline
                    path={rider_polyline}
                    strokeColor={theme.palette.bearer.orange}
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            }
            {/* rider_polyline added by Bearer Web Developer end*/}
            {/* modified by Bearer Web Developer start*/}
            {/* rider_polyline condition added by Bearer Web Developer */}
            {
                p_points && !rider_polyline &&
                <Polyline
                    path={p_points}
                    strokeColor={themeMode === "dark"? "#4ABF12": "#14438E"} // modified by Bearer Web Developer
                    strokeOpacity={1}
                    strokeWeight={4}
                />
            }
            {/* modified by Bearer Web Developer end*/}
            {
                !p_points && pathPreview &&
                <Polyline
                    path={pathPreview as Location[]}
                    strokeColor={themeMode === "dark"? "#4ABF12": "#14438E" } // modified by Bearer Web Developer
                    strokeOpacity={0}
                    icons={[
                        {
                            icon: {
                                path: "M 0,-1 0,1",
                                strokeOpacity: 1,
                                strokeWeight: 2,
                                scale: 1,
                            },
                            offset: "0",
                            repeat: "5px",
                        },
                    ]}
                />
            }
            {/* change current location icon and add theme condition by Bearer Web Developer start */}
            {!p_points &&
                <svg className={styles.currentIcon} onClick={getLocation}
                    xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <circle cx="24" cy="24" r="24" fill={themeMode === "dark" ? "#4D4D4D" : "#14438E"} />
                    <path d="M22.9998 34.9508V32.9508C20.9165 32.7174 19.129 31.8549 17.6373 30.3633C16.1456 28.8716 15.2831 27.0841 15.0498 25.0008H13.0498V23.0008H15.0498C15.2831 20.9174 16.1456 19.1299 17.6373 17.6383C19.129 16.1466 20.9165 15.2841 22.9998 15.0508V13.0508H24.9998V15.0508C27.0831 15.2841 28.8706 16.1466 30.3623 17.6383C31.854 19.1299 32.7165 20.9174 32.9498 23.0008H34.9498V25.0008H32.9498C32.7165 27.0841 31.854 28.8716 30.3623 30.3633C28.8706 31.8549 27.0831 32.7174 24.9998 32.9508V34.9508H22.9998ZM23.9998 31.0008C25.9331 31.0008 27.5831 30.3174 28.9498 28.9508C30.3165 27.5841 30.9998 25.9341 30.9998 24.0008C30.9998 22.0674 30.3165 20.4174 28.9498 19.0508C27.5831 17.6841 25.9331 17.0008 23.9998 17.0008C22.0665 17.0008 20.4165 17.6841 19.0498 19.0508C17.6831 20.4174 16.9998 22.0674 16.9998 24.0008C16.9998 25.9341 17.6831 27.5841 19.0498 28.9508C20.4165 30.3174 22.0665 31.0008 23.9998 31.0008ZM23.9998 28.0008C22.8998 28.0008 21.9581 27.6091 21.1748 26.8258C20.3915 26.0424 19.9998 25.1008 19.9998 24.0008C19.9998 22.9008 20.3915 21.9591 21.1748 21.1758C21.9581 20.3924 22.8998 20.0008 23.9998 20.0008C25.0998 20.0008 26.0415 20.3924 26.8248 21.1758C27.6081 21.9591 27.9998 22.9008 27.9998 24.0008C27.9998 25.1008 27.6081 26.0424 26.8248 26.8258C26.0415 27.6091 25.0998 28.0008 23.9998 28.0008ZM23.9998 26.0008C24.5498 26.0008 25.0206 25.8049 25.4123 25.4133C25.804 25.0216 25.9998 24.5508 25.9998 24.0008C25.9998 23.4508 25.804 22.9799 25.4123 22.5883C25.0206 22.1966 24.5498 22.0008 23.9998 22.0008C23.4498 22.0008 22.979 22.1966 22.5873 22.5883C22.1956 22.9799 21.9998 23.4508 21.9998 24.0008C21.9998 24.5508 22.1956 25.0216 22.5873 25.4133C22.979 25.8049 23.4498 26.0008 23.9998 26.0008Z" fill="#F5F5F5" />
                </svg>
            }
            {/* change current location icon and add theme condition by Bearer Web Developer end */}
            {/* added by Bearer Web Developer start - shortcutReservedOrders */}
            { appState?.ReservedOrders?.todayReservedOrdersList && 
                (
                    appState?.ReservedOrders?.shortcutReservedOrdersPlan === ShortcutReservedOrdersPlan.B ||
                    appState?.ReservedOrders?.shortcutReservedOrdersPlan === ShortcutReservedOrdersPlan.ShowAtFirstForPlanB
                    ) &&
                !isMobile &&
                    <ShortcutOrders />
            } 
            {/* added by Bearer Web Developer end - shortcutReservedOrders */}
        </Map>
    )
});
const BearerMapApi: React.FC = observer(() => {
    return (<BearerMapProvider><MapApi /></BearerMapProvider>)
})
export default BearerMapApi;
